import { useState, useEffect, useCallback } from "react";
import EmptyState from "./EmptyState";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", ...(getToken()?{Authorization:"Bearer "+getToken()}:{}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const ROLES = [
  { id:"admin",   label:"Admin",   color:"#dc2626", bg:"#fef2f2",  desc:"Full access to everything" },
  { id:"manager", label:"Manager", color:"#2563eb", bg:"#eff6ff",  desc:"Orders, inventory, logistics, reports" },
  { id:"staff",   label:"Staff",   color:"#15803d", bg:"#f0fdf4",  desc:"View + create invoices and shipments" },
  { id:"custom",  label:"Custom",  color:"#7c3aed", bg:"#f5f3ff",  desc:"Define custom permissions" },
];

const PERMISSIONS = [
  { section:"Inventory",  perms:[{ id:"inventory_view",label:"View"}, {id:"inventory_edit",label:"Edit"}] },
  { section:"Orders",     perms:[{ id:"orders_view",   label:"View"}, {id:"orders_edit",   label:"Edit"}] },
  { section:"Invoices",   perms:[{ id:"invoices_view", label:"View"}, {id:"invoices_create",label:"Create"}] },
  { section:"Purchases",  perms:[{ id:"purchases_view",label:"View"}, {id:"purchases_create",label:"Create"}] },
  { section:"Returns",    perms:[{ id:"returns_view",  label:"View"}, {id:"returns_create", label:"Create"}] },
  { section:"Logistics",  perms:[{ id:"logistics_view",label:"View"}, {id:"logistics_create",label:"Create"}] },
  { section:"Reports",    perms:[{ id:"reports_view",  label:"View"}, {id:"reports_export",  label:"Export"}] },
  { section:"WMS",        perms:[{ id:"wms_view",      label:"View"}, {id:"wms_edit",        label:"Edit"}] },
  { section:"Catalog",    perms:[{ id:"pim_view",      label:"View"}, {id:"pim_edit",        label:"Edit"}] },
  { section:"Admin Only", perms:[{ id:"settings_view", label:"Settings"},{id:"billing_view",label:"Billing"},{id:"team_manage",label:"Manage Team"}] },
];

const STATUS_COLORS = {
  active:    { color:"#15803d", bg:"#f0fdf4" },
  invited:   { color:"#d97706", bg:"#fffbeb" },
  suspended: { color:"#dc2626", bg:"#fef2f2" },
};

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:   { width:"100%", padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", marginBottom:10 },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" },
  modal:   { background:"#fff", borderRadius:12, padding:24, width:600, maxWidth:"96vw", maxHeight:"92vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" },
  toggle:  (on) => ({ width:40, height:22, borderRadius:11, background:on?"#22c55e":"#d1d5db", position:"relative", cursor:"pointer", transition:"background .2s", border:"none", flexShrink:0 }),
  knob:    (on) => ({ width:18, height:18, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:on?20:2, transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }),
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background:bg, color, padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:600 }}>{label}</span>
);

const EMPTY_FORM = { name:"", email:"", role:"staff", phone:"", notes:"", permissions:{} };

export default function PageTeam({ toast, tenant }) {
  const [members,    setMembers]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [showModal,  setShowModal]  = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [showPerms,  setShowPerms]  = useState(false);
  const [inviteUrl,  setInviteUrl]  = useState("");

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api("/team");
      setMembers(data.members || []);
    } catch(e) { toast(e.message, "error"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const openInvite = () => {
    setEditMember(null);
    setForm(EMPTY_FORM);
    setShowPerms(false);
    setInviteUrl("");
    setShowModal(true);
  };

  const openEdit = (member) => {
    setEditMember(member);
    setForm({
      name:        member.name,
      email:       member.email,
      role:        member.role,
      phone:       member.phone || "",
      notes:       member.notes || "",
      permissions: member.permissions || {},
    });
    setShowPerms(member.role === "custom");
    setInviteUrl("");
    setShowModal(true);
  };

  const handleRoleChange = (role) => {
    setForm((f) => ({ ...f, role }));
    setShowPerms(role === "custom");
  };

  const togglePerm = (permId) => {
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [permId]:!f.permissions[permId] } }));
  };

  const saveMember = async () => {
    if (!form.name || !form.email) { toast("Name and email required", "error"); return; }
    setSaving(true);
    try {
      if (editMember) {
        await api("/team/" + editMember._id, { method:"PUT", body:JSON.stringify(form) });
        toast("Team member updated ✓", "success");
        setShowModal(false);
        loadMembers();
      } else {
        const data = await api("/team/invite", { method:"POST", body:JSON.stringify(form) });
        toast("Invite sent to " + form.email + " ✓", "success");
        setInviteUrl(data.inviteUrl);
        loadMembers();
      }
    } catch(e) { toast(e.message, "error"); }
    finally { setSaving(false); }
  };

  const removeMember = async (id, name) => {
    if (!window.confirm("Remove " + name + " from your team?")) return;
    try {
      await api("/team/" + id, { method:"DELETE" });
      toast("Member removed ✓", "success");
      loadMembers();
    } catch(e) { toast(e.message, "error"); }
  };

  const toggleStatus = async (member) => {
    const newStatus = member.status === "suspended" ? "active" : "suspended";
    try {
      await api("/team/" + member._id, { method:"PUT", body:JSON.stringify({ status:newStatus }) });
      toast(newStatus === "active" ? "Member activated ✓" : "Member suspended ✓", "success");
      loadMembers();
    } catch(e) { toast(e.message, "error"); }
  };

  const resendInvite = async (id, email) => {
    try {
      await api("/team/" + id + "/resend-invite", { method:"POST", body:"{}" });
      toast("Invite resent to " + email + " ✓", "success");
    } catch(e) { toast(e.message, "error"); }
  };

  const activeCount   = members.filter((m) => m.status==="active").length;
  const invitedCount  = members.filter((m) => m.status==="invited").length;
  const planLimits    = { trial:2, starter:3, growth:10, enterprise:999 };
  const maxMembers    = planLimits[tenant?.plan] || 2;

  return (
    <div>
      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
        {[
          { label:"Total Members", value:members.length,       color:"#2563eb" },
          { label:"Active",        value:activeCount,           color:"#15803d" },
          { label:"Pending Invite",value:invitedCount,          color:"#d97706" },
          { label:"Plan Limit",    value:maxMembers,            color:"#F47920" },
        ].map((k) => (
          <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px" }}>
            <div style={{ fontSize:11, color:"#6b7280", marginBottom:6 }}>{k.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* plan warning */}
      {members.length >= maxMembers && (
        <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:13, color:"#92400e" }}>
          ⚠️ You've reached your plan limit of {maxMembers} team members. <strong>Upgrade your plan</strong> to add more.
        </div>
      )}

      {/* header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ fontSize:13, color:"#6b7280" }}>
          {members.length} / {maxMembers} team members used
        </div>
        <button style={S.btn()} onClick={openInvite} disabled={members.length >= maxMembers}>
          + Invite Team Member
        </button>
      </div>

      {/* members list */}
      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>Loading…</div>
      ) : members.length === 0 ? (
        <EmptyState
          icon="👥"
          title="No team members yet"
          subtitle="Invite warehouse staff, managers or your accountant. Set what each person can see and do — they get their own login."
          actionLabel="+ Invite your first member"
          onAction={openInvite}
          columns={["Name","Role","Access","Status"]}
          exampleRows={[
            ["Priya Sharma","Manager","Orders, Inventory","Active"],
            ["Rahul Verma","Packer","Orders only","Active"],
            ["CA Office","Accountant","GST, Reports","Invited"],
          ]}
        />
      ) : (
        <div style={{ display:"grid", gap:10 }}>
          {members.map((member) => {
            const role = ROLES.find((r) => r.id===member.role) || ROLES[2];
            const st   = STATUS_COLORS[member.status] || STATUS_COLORS.invited;
            return (
              <div key={member._id} style={{ ...S.card, marginBottom:0 }}>
                <div style={{ padding:16, display:"flex", alignItems:"center", gap:16 }}>
                  {/* avatar */}
                  <div style={{ width:44, height:44, borderRadius:"50%", background:role.color, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:18, flexShrink:0 }}>
                    {member.name.charAt(0).toUpperCase()}
                  </div>

                  {/* info */}
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
                      <span style={{ fontWeight:700, fontSize:15 }}>{member.name}</span>
                      <Badge label={role.label} color={role.color} bg={role.bg}/>
                      <Badge label={member.status} color={st.color} bg={st.bg}/>
                    </div>
                    <div style={{ fontSize:12, color:"#6b7280", display:"flex", gap:16 }}>
                      <span>✉️ {member.email}</span>
                      {member.phone && <span>📞 {member.phone}</span>}
                      {member.lastLoginAt && <span>🕐 Last login: {new Date(member.lastLoginAt).toLocaleDateString("en-IN")}</span>}
                      {member.joinedAt && <span>✅ Joined: {new Date(member.joinedAt).toLocaleDateString("en-IN")}</span>}
                      {member.status==="invited" && <span style={{ color:"#d97706" }}>⏳ Invite pending</span>}
                    </div>
                    {/* permission summary */}
                    <div style={{ display:"flex", gap:4, marginTop:8, flexWrap:"wrap" }}>
                      {Object.entries(member.permissions||{}).filter(([,v])=>v).slice(0,6).map(([key]) => (
                        <span key={key} style={{ fontSize:10, background:"#f0fdf4", color:"#15803d", padding:"1px 6px", borderRadius:10 }}>
                          {key.replace("_"," ")}
                        </span>
                      ))}
                      {Object.entries(member.permissions||{}).filter(([,v])=>v).length > 6 && (
                        <span style={{ fontSize:10, color:"#9ca3af" }}>+{Object.entries(member.permissions||{}).filter(([,v])=>v).length-6} more</span>
                      )}
                    </div>
                  </div>

                  {/* actions */}
                  <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                    {member.status==="invited" && (
                      <button style={{ ...S.btn("#d97706"), fontSize:11, padding:"4px 10px" }}
                        onClick={() => resendInvite(member._id, member.email)}>
                        ↻ Resend
                      </button>
                    )}
                    <button style={{ ...S.btnOut, fontSize:11 }} onClick={() => openEdit(member)}>Edit</button>
                    <button style={{ ...S.btnOut, fontSize:11, color:member.status==="suspended"?"#15803d":"#d97706", borderColor:member.status==="suspended"?"#bbf7d0":"#fde68a" }}
                      onClick={() => toggleStatus(member)}>
                      {member.status==="suspended" ? "Activate" : "Suspend"}
                    </button>
                    <button style={{ ...S.btnOut, fontSize:11, color:"#dc2626", borderColor:"#fecaca" }}
                      onClick={() => removeMember(member._id, member.name)}>Remove</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* role info cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginTop:20 }}>
        {ROLES.map((role) => (
          <div key={role.id} style={{ background:"#fff", borderRadius:10, border:`1px solid ${role.color}30`, borderTop:`3px solid ${role.color}`, padding:14 }}>
            <div style={{ fontWeight:700, color:role.color, marginBottom:6 }}>{role.label}</div>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:10 }}>{role.desc}</div>
            <div style={{ fontSize:11, color:"#9ca3af" }}>
              {members.filter((m) => m.role===role.id).length} member{members.filter((m)=>m.role===role.id).length!==1?"s":""}
            </div>
          </div>
        ))}
      </div>

      {/* ══ INVITE / EDIT MODAL ══ */}
      {showModal && (
        <div style={S.overlay} onClick={(e) => e.target===e.currentTarget && setShowModal(false)}>
          <div style={S.modal}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <span style={{ fontWeight:700, fontSize:16 }}>{editMember ? "Edit Team Member" : "Invite Team Member"}</span>
              <button onClick={() => setShowModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
            </div>

            {/* invite success */}
            {inviteUrl && (
              <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:14, marginBottom:16 }}>
                <div style={{ fontWeight:600, color:"#15803d", marginBottom:8 }}>✓ Invite sent to {form.email}</div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:8 }}>Share this link if email doesn't arrive:</div>
                <div style={{ fontFamily:"monospace", fontSize:11, background:"#fff", padding:"8px 10px", borderRadius:6, border:"1px solid #e5e7eb", wordBreak:"break-all" }}>{inviteUrl}</div>
                <button style={{ ...S.btn(), marginTop:10, fontSize:12 }} onClick={() => { navigator.clipboard.writeText(inviteUrl); toast("Link copied ✓","success"); }}>📋 Copy Link</button>
              </div>
            )}

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div>
                <label style={S.label}>Full Name *</label>
                <input style={S.input} placeholder="e.g. Rahul Sharma" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name:e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Email *</label>
                <input style={S.input} type="email" placeholder="rahul@yourstore.com" value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email:e.target.value }))} disabled={!!editMember}/>
              </div>
              <div>
                <label style={S.label}>Phone</label>
                <input style={S.input} placeholder="+91 98XXXXXXXX" value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone:e.target.value }))}/>
              </div>
              <div>
                <label style={S.label}>Notes</label>
                <input style={S.input} placeholder="e.g. Warehouse manager, Delhi" value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes:e.target.value }))}/>
              </div>
            </div>

            {/* role selection */}
            <label style={S.label}>Role *</label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:14 }}>
              {ROLES.map((role) => (
                <button key={role.id} onClick={() => handleRoleChange(role.id)}
                  style={{ padding:"10px 8px", borderRadius:8, border:`2px solid ${form.role===role.id?role.color:"#e5e7eb"}`, background:form.role===role.id?role.bg:"#fff", cursor:"pointer", fontFamily:"inherit", textAlign:"center" }}>
                  <div style={{ fontWeight:700, fontSize:12, color:form.role===role.id?role.color:"#374151", marginBottom:2 }}>{role.label}</div>
                  <div style={{ fontSize:10, color:"#9ca3af" }}>{role.desc.split(" ").slice(0,3).join(" ")}…</div>
                </button>
              ))}
            </div>

            {/* permission matrix for custom role */}
            {(showPerms || form.role==="custom") && (
              <div style={{ background:"#f9fafb", borderRadius:8, padding:14, marginBottom:14 }}>
                <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>Custom Permissions</div>
                {PERMISSIONS.map((section) => (
                  <div key={section.section} style={{ marginBottom:12 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:"#374151", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.5px" }}>{section.section}</div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      {section.perms.map((perm) => {
                        const isOn = form.permissions[perm.id];
                        return (
                          <label key={perm.id} style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer", fontSize:12 }}>
                            <input type="checkbox" checked={!!isOn} onChange={() => togglePerm(perm.id)}/>
                            {perm.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* role preview */}
            {form.role !== "custom" && (
              <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:12, marginBottom:14, fontSize:12 }}>
                <div style={{ fontWeight:600, color:"#15803d", marginBottom:6 }}>{ROLES.find((r)=>r.id===form.role)?.label} permissions:</div>
                <div style={{ color:"#374151" }}>{ROLES.find((r)=>r.id===form.role)?.desc}</div>
              </div>
            )}

            {!inviteUrl && (
              <button style={{ ...S.btn(), width:"100%", padding:11, fontSize:14, opacity:saving?0.7:1 }}
                onClick={saveMember} disabled={saving}>
                {saving ? "Saving…" : editMember ? "Update Member" : "📧 Send Invite"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
